import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import classNames from 'classnames';
import { UXPContext } from '@core/context/Context';
import { ThemeType } from '@types';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import ButtonGroup from '@widget/buttons/ButtonGroup';
import PortalContainer from '@widget/popups/PortalContainer';
import IconButton from '@widget/buttons/IconButton';
function getMonacoLib() {
    return window.UXPMonaco;
}
function toMonacoLanguage(lang) {
    return lang === 'text' ? 'plaintext' : lang;
}
const TAB_SIZE_VALUES = [2, 4, 8];
/**
 * @export
 * Monaco-backed code editor with syntax highlighting, a toolbar (Format,
 * tab-size cycle, line numbers, fullscreen), and optional auto-grow.
 *
 * Supports JS/TS extra libs for inline IntelliSense (pass type declaration
 * strings via `extraLibs`). Exposes a ref handle ({@link CodeEditorHandlers})
 * for programmatic focus and validation.
 *
 * @example
 * ```tsx
 * <CodeEditor value={code} onChange={setCode} language="javascript" height="300px" />
 * ```
 *
 * @example
 * ```tsx
 * // With ref handle for validation
 * const ref = useRef<CodeEditorHandlers>(null);
 * <CodeEditor ref={ref} value={code} onChange={setCode} language="json" />
 * const { valid, error } = ref.current?.validate() ?? { valid: true, error: '' };
 * ```
 */
const CodeEditor = forwardRef((props, ref) => {
    var _a, _b, _c;
    const context = useContext(UXPContext);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showLineNumbers, setShowLineNumbers] = useState((_a = props.lineNumbers) !== null && _a !== void 0 ? _a : false);
    const [tabSize, setTabSize] = useState((_b = props.indentUnit) !== null && _b !== void 0 ? _b : 4);
    const { value, onChange, language = 'text', onFocus, onBlur, className, placeholder, extraLibs, readOnly, disabled, minWidth, maxWidth, width, fullWidth, isValid, height, minHeight, maxHeight, autoHeight, hideToolbar, compactToolbar, } = props;
    const containerRef = useRef(null);
    const editorRef = useRef(null);
    const fullscreenEditorRef = useRef(null);
    const monacoRef = useRef(null);
    const extraLibDisposablesRef = useRef([]);
    const valueRef = useRef(value);
    valueRef.current = value;
    const autoHeightRef = useRef(autoHeight);
    autoHeightRef.current = autoHeight;
    const [contentHeight, setContentHeight] = useState(100);
    const isReadOnly = readOnly || disabled;
    const isDark = context.themeType === ThemeType.Dark;
    useEffect(() => {
        const monaco = monacoRef.current;
        if (!monaco || !extraLibs)
            return;
        extraLibDisposablesRef.current.forEach(d => d.dispose());
        extraLibDisposablesRef.current = [];
        const isTS = language === 'typescript';
        const defaults = isTS
            ? monaco.languages.typescript.typescriptDefaults
            : monaco.languages.typescript.javascriptDefaults;
        extraLibDisposablesRef.current = extraLibs.map(lib => defaults.addExtraLib(lib.content, lib.filename));
        return () => {
            extraLibDisposablesRef.current.forEach(d => d.dispose());
            extraLibDisposablesRef.current = [];
        };
    }, [extraLibs, language]);
    useEffect(() => {
        return () => {
            extraLibDisposablesRef.current.forEach(d => d.dispose());
        };
    }, []);
    useImperativeHandle(ref, () => ({
        focus: () => { var _a; return (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
        validate: () => {
            var _a, _b;
            const monaco = monacoRef.current;
            const editor = editorRef.current;
            const code = valueRef.current;
            if (!monaco || !editor)
                return { valid: true, error: '', code };
            const model = editor.getModel();
            if (!model)
                return { valid: true, error: '', code };
            const markers = monaco.editor
                .getModelMarkers({ resource: model.uri })
                .filter((m) => m.severity === monaco.MarkerSeverity.Error);
            return {
                valid: markers.length === 0,
                error: (_b = (_a = markers[0]) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : '',
                code,
            };
        },
    }));
    const handleMount = useCallback((editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
        if (language === 'javascript' || language === 'typescript') {
            const isTS = language === 'typescript';
            const defaults = isTS
                ? monaco.languages.typescript.typescriptDefaults
                : monaco.languages.typescript.javascriptDefaults;
            defaults.setCompilerOptions({
                target: monaco.languages.typescript.ScriptTarget.ES2015,
                allowNonTsExtensions: true,
                noLib: extraLibs && extraLibs.length > 0,
            });
            if (extraLibs === null || extraLibs === void 0 ? void 0 : extraLibs.length) {
                extraLibDisposablesRef.current.forEach(d => d.dispose());
                extraLibDisposablesRef.current = extraLibs.map(lib => defaults.addExtraLib(lib.content, lib.filename));
            }
        }
        if (onFocus) {
            editor.onDidFocusEditorWidget(() => {
                var _a;
                (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.add('uxpcore_input--focus');
                if (!isReadOnly)
                    onFocus();
            });
        }
        editor.onDidBlurEditorWidget(() => {
            var _a;
            (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove('uxpcore_input--focus');
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(valueRef.current);
        });
        if (autoHeightRef.current) {
            const updateHeight = () => {
                const h = editor.getContentHeight();
                setContentHeight(h);
                editor.layout();
            };
            updateHeight();
            editor.onDidContentSizeChange(updateHeight);
        }
    }, [language, extraLibs, isReadOnly, onFocus, onBlur]);
    const handleFullscreenMount = useCallback((editor) => {
        fullscreenEditorRef.current = editor;
    }, []);
    const handleChange = useCallback((val) => {
        if (isReadOnly)
            return;
        onChange(val !== null && val !== void 0 ? val : '');
    }, [isReadOnly, onChange]);
    const handleFormat = useCallback(() => {
        var _a;
        const editor = isFullscreen ? fullscreenEditorRef.current : editorRef.current;
        if (!editor)
            return;
        editor.updateOptions({ tabSize });
        (_a = editor.getModel()) === null || _a === void 0 ? void 0 : _a.updateOptions({ tabSize, indentSize: tabSize, insertSpaces: true });
        editor.trigger('keyboard', 'editor.action.formatDocument', {});
    }, [tabSize, isFullscreen]);
    useEffect(() => {
        if (!isFullscreen)
            return;
        const onKeyDown = (e) => { if (e.key === 'Escape')
            setIsFullscreen(false); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isFullscreen]);
    const editorOptions = useMemo(() => ({
        readOnly: isReadOnly,
        lineNumbers: showLineNumbers ? 'on' : 'off',
        tabSize,
        fontSize: 13,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        folding: false,
        placeholder,
        glyphMargin: false,
        lineDecorationsWidth: showLineNumbers ? undefined : 4,
        renderLineHighlight: 'none',
        overviewRulerLanes: 0,
        scrollbar: autoHeight
            ? { vertical: 'hidden', horizontal: 'hidden', alwaysConsumeMouseWheel: false, handleMouseWheel: false }
            : { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
    }), [isReadOnly, showLineNumbers, tabSize, placeholder, autoHeight]);
    const handleCycleTabSize = useCallback(() => {
        setTabSize(prev => {
            const idx = TAB_SIZE_VALUES.indexOf(prev);
            return TAB_SIZE_VALUES[(idx + 1) % TAB_SIZE_VALUES.length];
        });
    }, []);
    const toolbarButtons = useMemo(() => {
        const items = [];
        if (!isReadOnly) {
            items.push({
                id: 'format',
                title: 'Format',
                leftIcon: 'fal magic',
                size: 'small',
                onClick: handleFormat,
            });
        }
        items.push({
            id: 'tabsize',
            title: `${tabSize} Spaces`,
            leftIcon: 'fal indent',
            onClick: handleCycleTabSize,
            size: 'small',
        });
        items.push({
            id: 'lines',
            title: 'Lines',
            leftIcon: 'fal list-ol',
            onClick: () => setShowLineNumbers(l => !l),
            size: 'small',
        });
        items.push({
            id: 'fullscreen',
            title: isFullscreen ? 'Collapse' : 'Expand',
            leftIcon: isFullscreen ? 'fal compress' : 'fal expand',
            onClick: () => setIsFullscreen(f => !f),
            size: 'small'
        });
        return items;
    }, [isReadOnly, handleFormat, tabSize, handleCycleTabSize, isFullscreen]);
    const sizeStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, height, minHeight, maxHeight });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid });
    const uxpMonaco = getMonacoLib();
    const Editor = uxpMonaco === null || uxpMonaco === void 0 ? void 0 : uxpMonaco.Editor;
    const toolbar = hideToolbar ? null : compactToolbar ? (_jsxs("div", { className: "uxpcore_codeeditor__toolbar uxpcore_codeeditor__toolbar--compact", children: [!isReadOnly && (_jsx(IconButton, { icon: "fal magic", variant: "secondary", borderless: true, onClick: handleFormat })), _jsx(IconButton, { icon: "fal indent", variant: "secondary", borderless: true, onClick: handleCycleTabSize }), _jsx(IconButton, { icon: "fal list-ol", variant: showLineNumbers ? 'primary' : 'secondary', borderless: true, onClick: () => setShowLineNumbers(l => !l) }), _jsx(IconButton, { icon: isFullscreen ? 'fal compress' : 'fal expand', variant: "secondary", borderless: true, onClick: () => setIsFullscreen(f => !f) })] })) : (_jsx("div", { className: "uxpcore_codeeditor__toolbar", children: _jsx(ButtonGroup, { variant: "secondary", buttons: toolbarButtons, activeId: showLineNumbers ? 'lines' : undefined }) }));
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: containerRef, className: classNames('uxpcore_input', 'uxpcore_input--codeeditor', stateClass, className, {
                    'uxpcore_input--full-width': fullWidth,
                    'uxpcore_input--has-content': ((_c = value === null || value === void 0 ? void 0 : value.length) !== null && _c !== void 0 ? _c : 0) > 0,
                    'uxpcore_input--codeeditor--autoheight': autoHeight,
                }), style: autoHeight ? Object.assign(Object.assign({}, sizeStyles), { height: 'auto' }) : sizeStyles, children: [toolbar, _jsx("div", { className: "uxpcore_codeeditor__editor-area", style: autoHeight ? { height: contentHeight } : undefined, children: Editor && (_jsx(Editor, { value: value, language: toMonacoLanguage(language), theme: isDark ? 'vs-dark' : 'vs', onChange: handleChange, onMount: handleMount, height: autoHeight ? contentHeight : '100%', options: editorOptions })) })] }), isFullscreen && (_jsx(PortalContainer, { children: _jsxs("div", { className: "uxpcore_codeeditor__fullscreen-overlay", children: [toolbar, Editor && (_jsx(Editor, { value: value, language: toMonacoLanguage(language), theme: isDark ? 'vs-dark' : 'vs', onChange: handleChange, onMount: handleFullscreenMount, height: "100%", options: editorOptions }))] }) }))] }));
});
CodeEditor.displayName = 'CodeEditor';
export default CodeEditor;
